import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Activity, Hash, Timer } from "lucide-react";
import { scrollToSection } from "./Header";
import { COVER_ART } from "../constants/site";

const lines = [
  { text: "Keep your culture", accent: false },
  { text: "alive between", accent: false },
  { text: "all-hands.", accent: true },
];

const lineVariants = {
  hidden: { y: "110%" },
  show: { y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = ({ openLead }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, -70]);
  const rotate = useTransform(scrollY, [0, 700], [0, -3]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 pb-16 sm:pt-36 lg:pt-48 lg:pb-36"
    >
      <div className="pointer-events-none absolute -right-40 top-10 h-[34rem] w-[34rem] rounded-full bg-ochre/25 blur-3xl" />
      <div className="pointer-events-none absolute -left-52 bottom-0 h-[28rem] w-[28rem] rounded-full bg-sage/25 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-20 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-8 flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.25em] text-terracotta"
            data-testid="hero-overline"
          >
            <Activity className="h-3.5 w-3.5" />
            For remote &amp; hybrid teams
          </motion.p>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.13, delayChildren: 0.2 } } }}
            className="font-serif text-4xl font-medium leading-[1.04] tracking-tight text-walnut sm:text-6xl lg:text-[4.6rem]"
            data-testid="hero-headline"
          >
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  variants={lineVariants}
                  className={`block ${line.accent ? "italic text-terracotta" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-umber/90 md:text-lg"
            data-testid="hero-subheadline"
          >
            Turn company updates into clear recaps, meaningful recognition and next
            steps your team can see. Add a short team check-in when you want to
            understand what needs attention.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-demo-button"
              onClick={openLead}
              className="group flex items-center gap-2.5 rounded-full bg-terracotta px-7 py-4 text-sm font-semibold text-paper shadow-[0_12px_32px_rgba(202,93,67,0.35)] transition-colors duration-300 hover:bg-terracotta-dark"
            >
              Request Pricing
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-view-sample-button"
              onClick={() => scrollToSection("#sample-issue")}
              className="text-sm font-semibold text-umber underline underline-offset-4 transition-colors duration-300 hover:text-terracotta"
            >
              View Sample Issue
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-clay"
            data-testid="hero-note"
          >
            Send the deck — review the draft — you approve
          </motion.p>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div style={{ y, rotate }} className="relative mx-auto max-w-md">
              <div
                data-testid="hero-tablet-mockup"
                className="rounded-[2.5rem] border border-walnut bg-walnut p-3 shadow-[0_48px_90px_rgba(42,32,28,0.35)]"
              >
                <div className="relative aspect-[4/5] overflow-hidden rounded-[1.9rem] bg-walnut">
                  <img
                    src={COVER_ART}
                    alt="Cedar & Field Q3 2026 magazine cover"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-walnut/50 via-transparent to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 px-7 pb-6">
                    <div className="flex items-center gap-1.5">
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((d) => (
                        <span
                          key={d}
                          className={`h-1 rounded-full ${d === 0 ? "w-6 bg-terracotta" : "w-2.5 bg-paper/50"}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                className="absolute -left-8 top-16 flex items-center gap-2 rounded-full border border-line bg-paper px-4 py-2.5 shadow-[0_12px_30px_rgba(42,32,28,0.12)]"
                data-testid="hero-badge-turnaround"
              >
                <Timer className="h-4 w-4 text-terracotta" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-walnut">
                  Drafts in 2 business days
                </span>
              </motion.div>
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 1 }}
                className="absolute -right-6 bottom-24 flex items-center gap-2 rounded-full border border-line bg-paper px-4 py-2.5 shadow-[0_12px_30px_rgba(42,32,28,0.12)]"
                data-testid="hero-badge-slack"
              >
                <Hash className="h-4 w-4 text-olive" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-walnut">
                  Slack-ready text
                </span>
              </motion.div>
            </motion.div>
          </motion.div>
          <p
            className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-clay"
            data-testid="hero-sample-label"
          >
            Fictional sample issue — not a real client
          </p>
        </div>
      </div>
    </section>
  );
};
